import { FormikErrors } from 'formik';
import * as Yup from 'yup';
import { formatPhoneToE164 } from '../../../../utils/formatPhoneToE164';
import { type Checkbox } from '../../constants/ShareEvent.types';
import dataSources from '../../constants/dataSources';
import {
  FIELD_DIRECT_RECIPIENTS_SHARE,
  type FieldSharingMethod,
} from '../../constants/shareMethods';
import { type MultiInputTag } from './ShareEventRecipients';

export interface FieldFormValues {
  recipients: MultiInputTag[];
  sharingMethod: FieldSharingMethod;
  dataSources: Checkbox[];
  notes: string;
}

export type StepNumber = 1 | 2 | 3 | 4;

/** Create the validation schema based on the current step and sharing method */
export const createValidationSchema = (
  currentStep: StepNumber,
  sharingMethod: FieldSharingMethod,
) => {
  const baseValidation = {
    sharingMethod: Yup.string().required(),
    dataSources: Yup.array().test(
      'datasources-required',
      'Select at least one data source',
      function (value: Checkbox[] | undefined) {
        if (currentStep >= 2) {
          return Boolean(
            value && value.some((source: Checkbox) => source.isChecked),
          );
        }
        return true;
      },
    ),
    sessions: Yup.array(),
    notes: Yup.string(),
  };

  // Different validation based on sharing method
  const recipientsValidation =
    sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE
      ? Yup.array()
          .min(1, 'At least one recipient is required')
          .test(
            'recipients-valid',
            'Invalid email or phone number format',
            function (value: MultiInputTag[] | undefined) {
              if (currentStep >= 1 && value && value.length > 0) {
                return value.every(
                  (recipient: MultiInputTag) => recipient.isValid,
                );
              }
              return true;
            },
          )
          .test(
            'recipients-limit',
            'Limit reached: Total recipients cannot exceed 10.',
            function (value: MultiInputTag[] | undefined) {
              return !(currentStep >= 1 && value && value.length > 10);
            },
          )
      : Yup.array();

  return Yup.object().shape({
    ...baseValidation,
    recipients: recipientsValidation,
  });
};

export const createInitialValues = (
  sharingMethod: FieldSharingMethod,
): FieldFormValues => ({
  recipients: [],
  sharingMethod,
  dataSources: dataSources.map(source => ({ ...source, isChecked: false })), // TODO: update to true if checked by default
  notes: '',
});

// Step validation logic
export const isStepValid = (
  step: StepNumber,
  values: FieldFormValues,
  errors: FormikErrors<FieldFormValues>,
  sharingMethod: FieldSharingMethod,
): boolean => {
  switch (step) {
    case 1:
      // Check for Formik errors first
      if (errors.recipients || errors.sharingMethod) {
        return false;
      }

      // For direct recipients, also check that we have valid recipients
      if (sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE) {
        return (
          values.recipients.length > 0 &&
          values.recipients.every(recipient => recipient.isValid)
        );
      }

      // For share-link, no recipients needed
      return true;
    case 2:
      // Check for Formik errors first
      if (errors.dataSources) {
        return false;
      }

      // Also check that at least one data source is selected
      return values.dataSources.some(source => source.isChecked);
    case 3:
      return true; // Notes are optional
    case 4:
      return true; // Share link display step
    default:
      return false;
  }
};

export const createSubmissionPayload = (
  values: FieldFormValues,
  sharingMethod: FieldSharingMethod,
  uri: string,
  organization: string,
) => {
  return {
    uri,
    organization,
    recipients: {
      emails:
        sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE
          ? values.recipients
              .filter(
                recipient => recipient.type === 'email' && recipient.isValid,
              )
              .map(recipient => recipient.value)
          : [],
      phone_numbers:
        sharingMethod === FIELD_DIRECT_RECIPIENTS_SHARE
          ? values.recipients
              .filter(
                recipient => recipient.type === 'phone' && recipient.isValid,
              )
              .map(recipient => formatPhoneToE164(recipient.value))
          : [],
      organization_contacts: [],
      contact_groups: [],
    },
    display_config: values.dataSources
      .filter(source => source.isChecked)
      .map(source => ({
        name: source.id,
        meta: {},
      })),
    additional_comments: values.notes ? [values.notes] : [],
  };
};
